/* TrueHone gift-tier ladder — state machine + cart wiring (L-016).
 *
 * CONTENT-AGNOSTIC: every tier/gift/price/copy string is a theme setting
 * rendered by snippets/truehone-gift-ladder.liquid into DOM + a JSON config
 * block; this file never hardcodes a product, variant id, price, or copy
 * string (user-visible strings arrive via cfg.strings / hidden DOM notes).
 *
 * Encodes the two hard-won bug fixes from the Bright Sprouts free-gift build
 * (projects/bright-sprouts/decisions/20260617_decisions-free-gift-backend.md):
 *   1. ONE atomic /cart/add.js `items[]` call for tier + all unlocked gifts
 *      (two sequential adds double-render and leave the drawer unpainted).
 *   2. After renderContents, clear `is-empty` off <cart-drawer> AND
 *      .drawer__inner (the theme's product-form.js used to do this; once we
 *      own the add flow we must do it, or the first add from an empty cart
 *      opens a BLANK drawer).
 *
 * Cumulative per-tier gifts (new vs the references): selecting tier N unlocks
 * the gifts of every enabled tier <= N. Each unlocked gift is its own $0 cart
 * line, property-tagged `_truehone_gift` so the drawer AND the /cart page
 * lock it.
 *
 * TWO RUN MODES (QC punch-list fix #1):
 *   - FULL (PDP): a [data-truehone-ladder] root exists -> tier UI + submit
 *     intercept + reconciler. The parsed config is persisted to
 *     sessionStorage so the cart page can reconcile with full entitlement
 *     knowledge.
 *   - CART-GUARD (/cart page, loaded via templates/cart.json): no ladder
 *     root -> reconciler only. Config comes from sessionStorage when a PDP
 *     visit stored it; without it the guard runs CONSERVATIVE rules only
 *     (identify gift lines by the `_truehone_gift` property, normalize gift
 *     qty to 1, strip gifts when no paid line remains — it never strips a
 *     gift that might be earned, and never re-adds). After a guard mutation
 *     converges, the page reloads once so the server-rendered /cart rows
 *     match the reconciled cart.
 *
 * Blank-safe: a gift with no variant id configured (pre-provisioning) still
 * renders its locked/unlocked UI, but is silently skipped in the cart add.
 * No tier variant at all -> the add shows the (settings-driven) "not
 * available yet" note instead of silently no-oping, and never clears a cart
 * it can't refill.
 */
(function () {
  'use strict';

  var CFG_STORE_KEY = 'thLadderCfg';

  function init() {
    var root = document.querySelector('[data-truehone-ladder]');
    var guardMode = !root;
    if (root && root.__thInit) return;
    if (guardMode && window.__thGuardInit) return;
    if (root) root.__thInit = true;
    else window.__thGuardInit = true;

    var cfg = { tiers: [], defaultTier: 0, giftProperty: '_truehone_gift', strings: {} };
    var haveCfg = false;
    if (root) {
      var cfgEl = root.querySelector('script[data-th-ladder-config]');
      if (cfgEl) {
        try { cfg = JSON.parse(cfgEl.textContent); haveCfg = true; } catch (e) { /* keep defaults */ }
      }
      // persist for CART-GUARD mode on the /cart page (same session)
      try { sessionStorage.setItem(CFG_STORE_KEY, JSON.stringify(cfg)); } catch (e) { /* storage unavailable — guard falls back to conservative rules */ }
    } else {
      try {
        var stored = sessionStorage.getItem(CFG_STORE_KEY);
        if (stored) { cfg = JSON.parse(stored); haveCfg = true; }
      } catch (e) { /* conservative guard mode */ }
    }
    var GIFT_PROP = cfg.giftProperty || '_truehone_gift';
    var STRINGS = cfg.strings || {};
    var tiers = cfg.tiers || [];
    var giftVariantIds = [];
    tiers.forEach(function (t) {
      if (t.gift && t.gift.enabled && t.gift.variantId) giftVariantIds.push(t.gift.variantId);
      // BUNDLE COMPONENTS (2026-07-13): hidden $0 component products (chef's knife,
      // cutting board) ride the ENTIRE gift machinery — same property, same managed-id
      // list, same entitled-set — so the atomic add, orphan cleanup, qty normalize,
      // re-add, one-click bundle removal and conservative guard all apply for free.
      // The ONLY difference is no gift UI renders for them (config-only, no block UI).
      (t.components || []).forEach(function (cid) {
        if (cid && giftVariantIds.indexOf(cid) === -1) giftVariantIds.push(cid);
      });
    });

    var selectedTier = 1;
    var SECTION_IDS = 'cart-drawer,cart-icon-bubble';

    function tierByIndex(i) {
      for (var k = 0; k < tiers.length; k++) if (tiers[k].index === i) return tiers[k];
      return null;
    }
    function tierByVariant(vid) {
      for (var k = 0; k < tiers.length; k++) if (tiers[k].variantId === vid) return tiers[k];
      return null;
    }
    // SCOPE GUARD (cart-surface completion audit 2026-07-10): a /cart/add form
    // belongs to the LADDER only if its [name=id] is blank/unparsed
    // (pre-provisioning, harness) or points at a configured tier variant.
    // Any OTHER add form that can share the page (drawer upsell, quick-add
    // modal, featured-product) must NOT be hijacked — the package-selector
    // clear would wipe the customer's cart to add an upsell item.
    //
    // Legacy/no-config branch (verification-gap fix 2026-07-10): with
    // tiers.length === 0 there is no tier variant list to check membership
    // against, so `tierByVariant(v)` can never match — the SAME containment
    // check as the funded path (`!v || !!tierByVariant(v)`) degrades
    // correctly to `!v` on its own; it never needs a blanket `true`. A form
    // with a BLANK/unparsed [name=id] (pre-provisioning, the harness's own
    // form) still counts as the ladder's — that's the blank-safe path. A
    // form with a populated variant id can only be some OTHER product's add
    // form (a drawer upsell, quick-add) when we have zero tier config to
    // match it against, so it must pass through untouched.
    function isTierForm(form) {
      var input = form.querySelector('[name="id"]');
      var v = input ? parseInt(input.value, 10) : 0;
      return !v || !!tierByVariant(v);
    }
    // CUMULATIVE unlock rule: every enabled gift whose tier index <= the selected tier.
    function unlockedGifts(sel) {
      return tiers.filter(function (t) { return t.gift && t.gift.enabled && t.index <= sel; });
    }
    // CUMULATIVE component rule (mirrors gifts): every component variant id of every tier <= sel.
    function unlockedComponentIds(sel) {
      var out = [];
      tiers.forEach(function (t) {
        if (t.index <= sel && t.components) {
          t.components.forEach(function (cid) { if (cid && out.indexOf(cid) === -1) out.push(cid); });
        }
      });
      return out;
    }

    /* ===== Shared cart-flow state (fix #6: busy <-> reconcile coordination) ===== */
    var busy = false;               // an ATC add-cycle is in flight
    var pendingReconcile = false;   // a reconcile asked to run while busy
    var reconciling = false;        // a reconcile MUTATION is in flight
    var reconcileInFlight = null;   // promise of the current reconcile pass
    var lastAddedTier = null;       // fix #4: tier of the last SUCCESSFUL add
    var giftAddAttempted = {};      // fix #7: set ONLY after a FAILED gift re-add

    /* ===== FULL-mode-only elements (null in guard mode) ===== */
    var staleHintEl = root ? root.querySelector('[data-th-stale-hint]') : null;
    var unavailableEl = root ? root.querySelector('[data-th-unavailable]') : null;
    function showNote(el, on) { if (el) el.hidden = !on; }

    /* ===== Unlock bar: cumulative node lit-state + fill sweep (L-020m) =====
     * Replaces the old stacked locked<->unlocked panels. The bar is a FIXED
     * structure — this only toggles .is-lit on nodes whose tier index <= the
     * selected tier and sets the fill width to reach the highest lit node.
     * NOTHING here changes any element's height or position (zero bounce). */
    function updatePanels() {
      if (!root) return;
      var nodes = root.querySelectorAll('[data-th-unlock-node]');
      if (!nodes.length) return;
      var maxIndex = 0;
      nodes.forEach(function (node) {
        var req = parseInt(node.getAttribute('data-th-unlock-node'), 10) || 0;
        var lit = selectedTier >= req;
        node.classList.toggle('is-lit', lit);
        if (lit && req > maxIndex) maxIndex = req;
      });
      // Fill sweeps from node 1 to the highest lit node. With N nodes evenly
      // spaced along the rail (dot-1 center -> dot-N center), the fill covers
      // (maxIndex-1)/(N-1) of the rail width.
      var fill = root.querySelector('[data-th-unlock-fill]');
      if (fill) {
        var n = nodes.length;
        var pct = n > 1 ? ((maxIndex - 1) / (n - 1)) * 100 : (maxIndex >= 1 ? 100 : 0);
        if (pct < 0) pct = 0;
        if (pct > 100) pct = 100;
        fill.style.width = pct + '%';
      }
    }

    /* ===== Fix #3: honest unlock bar on the tier-only fallback path =====
     * When the combined add fails and we fall back to tier-only, the lit gift
     * nodes are a LIE — dim the whole bar (a settings-driven optional note can
     * ride the unavailable slot) until the next fully-successful add restores
     * the lit state. The unlock bar is a FIXED structure, so this is a pure
     * COLOR change (class toggle) — no bounce. */
    function unlockBar() {
      return root ? root.querySelector('[data-th-unlock]') : null;
    }
    function showGiftFail() {
      var bar = unlockBar();
      if (bar) bar.classList.add('th-unlock--failed');
    }
    function clearGiftFail() {
      var bar = unlockBar();
      if (bar) bar.classList.remove('th-unlock--failed');
    }

    /* ===== Fix #4: selector <-> cart divergence hint (never auto-mutates) ===== */
    function updateStaleHint() {
      showNote(staleHintEl, lastAddedTier !== null && selectedTier !== lastAddedTier);
    }
    function markAddedTier(sel, degraded) {
      lastAddedTier = sel;
      updateStaleHint();
      if (!degraded) clearGiftFail();
    }

    function setTier(i) {
      i = parseInt(i, 10);
      if (!i) return;
      selectedTier = i;
      if (!root) return;
      root.querySelectorAll('[data-th-tier-card]').forEach(function (card) {
        card.classList.toggle('is-selected', card.getAttribute('data-th-tier-card') === String(i));
      });
      // Mirror the variant id into every /cart/add form (the theme's own
      // variant JS does this too on a live store; this covers the harness and
      // is a harmless double-write in production).
      var t = tierByIndex(i);
      if (t && t.variantId) {
        document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach(function (input) {
          // scope guard: only retarget inputs already pointing at OUR tier
          // variants (or blank pre-provisioning) — never a drawer-upsell /
          // quick-add form's variant id.
          var cur = parseInt(input.value, 10);
          if (!cur || tierByVariant(cur)) input.value = t.variantId;
        });
      }
      showNote(unavailableEl, false); // a different tier may be available (fix #8)
      updateStaleHint();              // fix #4
      updatePanels();
    }

    if (root) {
      root.querySelectorAll('[data-th-tier]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) setTier(radio.getAttribute('data-th-tier'));
        });
      });

      // Initial state: the Liquid-checked radio; then apply the default-selected
      // setting (only when the URL doesn't pin a variant — a shared ?variant=
      // link must win over the merchandising default).
      var checked = root.querySelector('[data-th-tier]:checked');
      if (checked) selectedTier = parseInt(checked.getAttribute('data-th-tier'), 10) || 1;
      var urlPinsVariant = /[?&]variant=/.test(window.location.search);
      if (!urlPinsVariant && cfg.defaultTier > 0 && cfg.defaultTier !== selectedTier) {
        var defRadio = root.querySelector('[data-th-tier="' + cfg.defaultTier + '"]');
        if (defRadio) {
          defRadio.checked = true;
          // bubble a change so the theme's <variant-selects> syncs price/form
          defRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      setTier(selectedTier);
    }

    /* ===== Cart add: ONE atomic /cart/add.js call (tier + unlocked gifts) ===== */

    function renderDrawerAfterAdd(data) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && data && data.sections) {
        try {
          drawer.renderContents(data);
          // BLANK-DRAWER GUARD (Sprouts bug #1): renderContents only clears
          // is-empty off .drawer__inner in this theme; on the first add from
          // an EMPTY cart the <cart-drawer> element still carries is-empty and
          // the CSS hides the line items. Clear it on both.
          drawer.classList.remove('is-empty');
          var inner = drawer.querySelector('.drawer__inner');
          if (inner) inner.classList.remove('is-empty');
          return;
        } catch (e) { /* fall through to /cart */ }
      }
      window.location = '/cart';
    }

    function doAdd(body) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().then(function (j) { return { ok: r.ok, json: j }; });
      });
    }

    // Fix #5 companion: an OK add without a `sections` payload is still a
    // SUCCESS — re-fetch the sections separately instead of re-adding.
    function fetchSectionsFallback(json) {
      var out = json || {};
      return fetch(window.location.pathname + '?sections=' + SECTION_IDS, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) { return r.json(); })
        .then(function (sections) { if (sections) out.sections = sections; return out; })
        .catch(function () { return out; });
    }

    if (root && !window.__thSubmitBound) {
      window.__thSubmitBound = true;
      // CAPTURE phase on document: runs before <product-form>'s own submit
      // handler, which we fully replace (the "preventDefault + re-submit"
      // approach races the theme handler — proven in the Sprouts build).
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
        // scope guard: a non-ladder add form (drawer upsell, quick-add modal)
        // passes through to the theme's own product-form handler untouched.
        if (!isTierForm(form)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (busy) return;
        busy = true;
        // backstop: never let a hung add dead-button the page
        var busyTimeout = setTimeout(function () { busy = false; }, 8000);

        var idInput = form.querySelector('[name="id"]');
        var variantId = idInput ? parseInt(idInput.value, 10) : 0;
        var qtyInput = form.querySelector('[name="quantity"]');
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        // which tier is being bought — from the form's variant when we know
        // it, else the ladder's current selection
        var t = tierByVariant(variantId);
        var sel = t ? t.index : selectedTier;
        if (!variantId) {
          var st = tierByIndex(sel);
          if (st && st.variantId) variantId = st.variantId;
        }

        // Fix #9: real DOM disabled + aria-busy on the ATC buttons of BOTH
        // forms (main + sticky) during flight, restored after.
        function btnLoading(on) {
          // form.elements includes buttons associated via the form="" attribute
          // (the sticky ATC button lives OUTSIDE the form element — a
          // descendant-only selector missed it; completion-audit fix). Scoped
          // to ladder forms so an upsell button is never dead-locked.
          document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
            if (!isTierForm(f)) return;
            Array.prototype.forEach.call(f.elements, function (btn) {
              if (btn.type !== 'submit') return;
              btn.classList.toggle('loading', on);
              btn.disabled = on;
              if (on) {
                btn.setAttribute('aria-busy', 'true');
                btn.setAttribute('aria-disabled', 'true');
              } else {
                btn.removeAttribute('aria-busy');
                btn.removeAttribute('aria-disabled');
              }
              var sp = btn.querySelector('.loading-overlay__spinner');
              if (sp) sp.classList.toggle('hidden', !on);
            });
          });
        }

        if (!variantId) {
          // Pre-provisioning: nothing addable — do NOT clear the cart. Show
          // the visible settings-driven note instead of a silent no-op (fix #8).
          clearTimeout(busyTimeout);
          busy = false;
          showNote(unavailableEl, true);
          return;
        }
        showNote(unavailableEl, false);

        btnLoading(true);

        var tierItems = [{ id: variantId, quantity: qty }];
        // Blank-safe: gifts without a configured variant id are skipped
        // silently — the unlock UI still showed, the order still works.
        var giftItems = unlockedGifts(sel)
          .filter(function (g) { return g.gift.variantId; })
          .map(function (g) {
            var props = {};
            props[GIFT_PROP] = '1';
            return { id: g.gift.variantId, quantity: 1, properties: props };
          });
        // bundle components: their own $0 lines in the SAME atomic add (clean fulfillment)
        var componentItems = unlockedComponentIds(sel).map(function (cid) {
          var props = {};
          props[GIFT_PROP] = '1';
          return { id: cid, quantity: 1, properties: props };
        });
        var items = tierItems.concat(giftItems).concat(componentItems);
        var degraded = false;

        // PACKAGE-SELECTOR RULE: the tier IS the whole order — replace, never
        // accumulate. (wait for any in-flight reconcile: fix #6) ->
        // clear -> ONE atomic add -> render (one render).
        var waitReconcile = reconcileInFlight || Promise.resolve();
        waitReconcile
          .catch(function () { /* a failed reconcile never blocks the ATC */ })
          .then(function () { return fetch('/cart/clear.js', { method: 'POST' }); })
          .then(function () {
            return doAdd({ items: items, sections: SECTION_IDS, sections_url: window.location.pathname });
          })
          .then(function (res) {
            // Fix #5: gate the fallback on !res.ok ONLY. An OK response IS
            // success — never re-add the tier after ok:true (double-add bug);
            // a missing sections payload just means a separate sections fetch.
            if (res.ok) {
              markAddedTier(sel, false);
              if (res.json && res.json.sections) return res.json;
              return fetchSectionsFallback(res.json);
            }
            // combined add failed (e.g. a misconfigured gift SKU 422s the
            // whole items call) -> tier-only fallback so the customer never
            // loses their order to a gift problem.
            // RE-CLEAR first (completion audit 2026-07-10): shopify.dev's cart
            // reference documents only SINGLE-item error cases — it does NOT
            // guarantee a multi-item /cart/add.js is atomic on failure. If a
            // partial add landed the tier before the gift errored, a bare
            // re-add would double the tier line. The extra clear makes the
            // fallback idempotent either way.
            degraded = true;
            return fetch('/cart/clear.js', { method: 'POST' })
              .catch(function () { /* still attempt the tier-only add */ })
              .then(function () {
                return doAdd({ items: tierItems, sections: SECTION_IDS, sections_url: window.location.pathname });
              })
              .then(function (r2) {
                if (r2.ok) markAddedTier(sel, true);
                return r2.json;
              });
          })
          .then(function (data) {
            if (degraded) showGiftFail(); // fix #3: the panels stop celebrating
            renderDrawerAfterAdd(data);
          })
          .catch(function () { window.location = '/cart'; })
          .finally(function () {
            clearTimeout(busyTimeout);
            btnLoading(false);
            busy = false;
            if (pendingReconcile) { pendingReconcile = false; setTimeout(reconcile, 60); } // fix #6
          });
      }, true);
    }

    /* ===== Gift reconciler (cart edited in the drawer / on /cart / stale sessions) =====
     * The gift set is earned by the highest tier line in the cart. If the
     * customer removes the tier line (gifts orphaned) or an old session left
     * stale gift lines, reconcile: remove unearned gifts, normalize gift qty
     * to 1, re-add a missing earned gift. One mutation per pass; each pass
     * re-triggers until the cart converges.
     * CONSERVATIVE mode (guard mode without stored config): gift lines are
     * identified by property only; normalize qty to 1, strip gifts only when
     * no paid line remains; never re-add. */
    var guardMutated = false;
    var guardPassCount = 0;

    function isGiftLine(item) {
      if (!item) return false;
      if (item.properties && item.properties[GIFT_PROP]) return true;
      var vid = item.variant_id || item.id;
      return giftVariantIds.indexOf(vid) !== -1;
    }
    function tierIndexOfLine(item) {
      var t = tierByVariant(item.variant_id || item.id);
      return t ? t.index : 0;
    }
    function rerenderDrawer(data, toggleEmpty) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && data && data.sections && typeof drawer.renderContents === 'function') {
        try { drawer.renderContents(data); } catch (e) { /* non-fatal */ }
      }
      if (drawer && data && toggleEmpty) {
        // removing a gift can EMPTY the cart — renderContents injects the
        // empty-state sections but never toggles is-empty, which paints a
        // blank white panel (same class of bug as the first-add blank drawer)
        var empty = (typeof data.item_count === 'number') ? data.item_count === 0 : false;
        drawer.classList.toggle('is-empty', empty);
        var inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.toggle('is-empty', empty);
        var footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', empty);
      }
    }
    function changeLine(key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION_IDS, sections_url: window.location.pathname })
      }).then(function (r) { return r.json(); });
    }
    function scheduleNextPass() {
      if (guardPassCount < 6) setTimeout(reconcile, 60); // converge remaining lines (bounded)
    }
    function reconcile() {
      // Fix #6: never reconcile through an in-flight ATC add-cycle — the
      // clear+add would race it. Re-run once busy clears.
      if (busy) { pendingReconcile = true; return Promise.resolve(); }
      if (reconciling) return reconcileInFlight || Promise.resolve();
      guardPassCount++;
      var p = fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart || !cart.items) return;
          var maxTier = 0;
          var giftLines = [];
          var paidLines = 0;
          cart.items.forEach(function (it) {
            if (isGiftLine(it)) { giftLines.push(it); return; }
            paidLines++;
            var ti = tierIndexOfLine(it);
            if (ti > maxTier) maxTier = ti;
          });
          var entitledIds = null; // null => conservative mode
          if (haveCfg) {
            entitledIds = unlockedGifts(maxTier)
              .map(function (g) { return g.gift.variantId; })
              .filter(Boolean)
              // components are entitled exactly like gifts: earned by the highest tier
              // line in the cart — so orphan-strip, qty-normalize, and the missing-line
              // re-add (step 2) manage them with zero extra code paths.
              .concat(unlockedComponentIds(maxTier));
          }

          // 1) remove unearned gift lines / normalize earned gift qty to 1
          for (var i = 0; i < giftLines.length; i++) {
            var gl = giftLines[i];
            var vid = gl.variant_id || gl.id;
            var want;
            if (entitledIds) {
              want = entitledIds.indexOf(vid) !== -1 ? 1 : 0;
            } else {
              // conservative: keep (at qty 1) while ANY paid line exists;
              // strip only truly orphaned gifts
              want = paidLines > 0 ? 1 : 0;
            }
            if (gl.quantity !== want) {
              reconciling = true;
              guardMutated = true;
              return changeLine(gl.key, want)
                .then(function (data) { rerenderDrawer(data, true); })
                .finally(function () {
                  reconciling = false;
                  scheduleNextPass();
                });
            }
          }
          // 2) re-add a missing earned gift (tier line present, gift line gone)
          //    — only with full config (we need the variant ids)
          if (entitledIds) {
            var presentIds = giftLines.map(function (gl2) { return gl2.variant_id || gl2.id; });
            for (var j = 0; j < entitledIds.length; j++) {
              var gid = entitledIds[j];
              if (presentIds.indexOf(gid) === -1 && !giftAddAttempted[gid]) {
                reconciling = true;
                guardMutated = true;
                var props = {};
                props[GIFT_PROP] = '1';
                return doAdd({ items: [{ id: gid, quantity: 1, properties: props }], sections: SECTION_IDS, sections_url: window.location.pathname })
                  .then(function (res) {
                    if (res.ok) rerenderDrawer(res.json, false);
                    // Fix #7: flag ONLY on failure — a re-earned gift after a
                    // transient failure must stay re-addable; only a SKU that
                    // actually failed is one-shot (no retry loop).
                    else giftAddAttempted[gid] = true;
                  })
                  .catch(function () { giftAddAttempted[gid] = true; /* never block the cart on a gift */ })
                  // schedule the next pass like the qty/remove branch does —
                  // without it a SECOND missing gift (both deleted via API
                  // tampering) waited for the next page load (completion-audit fix)
                  .finally(function () { reconciling = false; scheduleNextPass(); });
              }
            }
          }
          // CONVERGED. In guard mode a mutation means the server-rendered
          // /cart rows are stale — reload once so the customer sees truth.
          // Reload ONLY where server-rendered cart rows exist (the /cart
          // page); on every other page (guard now loads globally) the drawer
          // re-render already shows truth and a reload would yank the
          // customer mid-browse (completion-audit fix).
          if (guardMode && guardMutated) {
            guardMutated = false;
            if (document.getElementById('main-cart-items')) window.location.reload();
          }
        })
        .catch(function () { /* never block the cart on a reconcile failure */ })
        .finally(function () { reconcileInFlight = null; });
      reconcileInFlight = p;
      return p;
    }

    // NETWORK hook: the drawer's qty stepper + remove button AND the /cart
    // page's quantity inputs all route through /cart/change|update — the
    // theme's pub/sub consts aren't reachable from here, so reconcile at the
    // fetch layer (theme-internals-independent).
    if (window.fetch && !window.__thFetchWrapped) {
      window.__thFetchWrapped = true;
      var origFetch = window.fetch;
      window.fetch = function () {
        var args = arguments;
        var url = (args[0] && args[0].url) ? args[0].url : String(args[0] || '');
        var isCartMutation = /\/cart\/(change|update)(\.js)?/.test(url);
        var p = origFetch.apply(this, args);
        if (isCartMutation && !reconciling) {
          // run AFTER the theme finishes its own render so we don't race it
          p.then(function () { guardPassCount = 0; setTimeout(reconcile, 60); }).catch(function () {});
        }
        return p;
      };
    }
    // belt-and-suspenders: subscribe to cartUpdate if the pub/sub is reachable
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          if (!reconciling) { guardPassCount = 0; setTimeout(reconcile, 60); }
        });
      }
    } catch (e) { /* optional */ }
    // reconcile once on load (bfcache restores, stale gifts from a prior
    // session, /cart landings with tampered gift lines)
    reconcile();

    /* ===== BFCACHE / back-button reset =====
     * A bfcache restore can freeze the page drawer-open + scroll-locked with
     * busy stuck true. Hard-reset the interactive state on a persisted show. */
    function resetInteractiveState() {
      busy = false;
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.remove('active', 'animate');
      document.body.classList.remove('overflow-hidden');
      document.querySelectorAll('.atc-button, .product-form__submit, [id^="StickyAtcSubmitButton-"], .main-product-atc').forEach(function (b) {
        b.classList.remove('loading');
        b.removeAttribute('aria-disabled');
        b.removeAttribute('aria-busy');
        b.disabled = false;
        var sp = b.querySelector('.loading-overlay__spinner');
        if (sp) sp.classList.add('hidden');
      });
    }
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) { resetInteractiveState(); guardPassCount = 0; reconcile(); }
    });
    window.addEventListener('pagehide', function () { busy = false; });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Section CTAs stay SCROLL links to the kit selector (#ws-buybox-top) — Sean's
 * locked call (2026-07-13, round 3): mid-page buttons jump BACK to the offer,
 * they do NOT add to cart. The button LABELS say so ("Choose your kit ↑") so
 * the copy is accurate. A brief real-ATC interception lived here for a few
 * hours the same day — reverted; do not re-add it. */
